/**
 * Profit Paths Component (MA-IMPL-120 - Phase 5 Upgraded)
 * 
 * MA-IMPL-120: Upgraded to show full end-to-end performance chain:
 * Campaign → Funnel → Lead → Appointment → Client → Revenue
 * 
 * Uses REAL data from attribution chain (not estimates).
 * Highlights strongest, weakest, and highest ROI paths.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Calendar,
  ArrowRight,
  Award,
  BarChart3,
  Route,
  Zap,
  AlertTriangle,
  Trophy,
  ChevronRight,
  GitBranch,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { api } from '../../lib/api';
import { HELPER_DEFINITIONS } from './HelperText';

// =============================================================================
// API HOOKS - MA-IMPL-120: Use V2 endpoint with real data
// =============================================================================

function useProfitPaths(period, sortBy, tenantId, campaignId, workspaceId, limit = 30) {
  return useQuery({
    queryKey: ['profit-paths-v2', period, sortBy, tenantId, campaignId, workspaceId, limit],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy, limit: String(limit) });
      if (tenantId) params.append('tenant_id', tenantId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (workspaceId) params.append('workspace_id', workspaceId);
      
      const res = await api.get(`/analytics/profit-paths-v2?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// METRIC HELPERS
// =============================================================================

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatNumber(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return String(value);
}

// =============================================================================
// PATH CARD COMPONENT - MA-IMPL-120: Full chain visibility
// =============================================================================

function ProfitPathCard({ path, maxRevenue }) {
  const revenuePercentage = maxRevenue > 0 ? (path.revenue / maxRevenue) * 100 : 0;
  const isTopPath = path.rank <= 3;
  
  // Determine path status badge
  const getStatusBadge = () => {
    if (path.is_strongest) return { label: 'Strongest', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Trophy };
    if (path.is_highest_roi) return { label: 'Best ROI', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Zap };
    if (path.is_weakest) return { label: 'Needs Work', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertTriangle };
    return null;
  };
  
  const statusBadge = getStatusBadge();
  
  return (
    <Card className={`bg-slate-800/50 border-slate-700/50 transition-all hover:border-slate-600 ${isTopPath ? 'ring-1 ring-indigo-500/30' : ''}`}>
      <CardContent className="p-5">
        {/* Header with Rank, Campaign, and Revenue */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg
              ${path.rank === 1 ? 'bg-amber-500/20 text-amber-400' : 
                path.rank === 2 ? 'bg-slate-400/20 text-slate-300' :
                path.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                'bg-slate-700/50 text-slate-400'}`}
            >
              #{path.rank}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{path.campaign_name}</span>
                {statusBadge && (
                  <Badge variant="outline" className={`${statusBadge.color} text-xs`}>
                    <statusBadge.icon className="w-3 h-3 mr-1" />
                    {statusBadge.label}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-400 mt-0.5">{path.funnel_name || 'Direct'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(path.revenue)}</p>
            <p className="text-xs text-slate-400">revenue</p>
          </div>
        </div>
        
        {/* MA-IMPL-120: Full Pipeline Chain Visualization */}
        <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
                {path.leads} Leads
              </Badge>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs">
                {path.appointments} Appts
              </Badge>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <Badge variant="outline" className="bg-indigo-500/10 text-indigo-400 border-indigo-500/30 text-xs">
                {path.shows} Shows
              </Badge>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                {path.clients} Clients
              </Badge>
            </div>
          </div>
          
          {/* Conversion rates chain */}
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
            <span>L→A: <span className={`${path.lead_to_appointment_rate >= 15 ? 'text-emerald-400' : path.lead_to_appointment_rate >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
              {path.lead_to_appointment_rate !== null ? `${path.lead_to_appointment_rate}%` : '—'}
            </span></span>
            <span>A→S: <span className={`${path.appointment_to_show_rate >= 50 ? 'text-emerald-400' : path.appointment_to_show_rate >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
              {path.appointment_to_show_rate !== null ? `${path.appointment_to_show_rate}%` : '—'}
            </span></span>
            <span>S→C: <span className={`${path.show_to_close_rate >= 20 ? 'text-emerald-400' : path.show_to_close_rate >= 10 ? 'text-amber-400' : 'text-red-400'}`}>
              {path.show_to_close_rate !== null ? `${path.show_to_close_rate}%` : '—'}
            </span></span>
          </div>
        </div>
        
        {/* Revenue Metrics */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-emerald-400 font-medium">{formatCurrency(path.revenue_per_lead || 0)}</span>
              <span className="text-slate-500 text-xs ml-1">/lead</span>
            </div>
            <div>
              <span className="text-slate-100 font-medium">
                {path.overall_conversion_rate !== null ? `${path.overall_conversion_rate}%` : '—'}
              </span>
              <span className="text-slate-500 text-xs ml-1">L→C</span>
            </div>
          </div>
          <div className="w-24">
            <Progress value={revenuePercentage} className="h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// SUMMARY CARDS
// =============================================================================

function SummaryCard({ title, value, subtitle, icon: Icon, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MAIN COMPONENT - MA-IMPL-120: Upgraded with full chain
// =============================================================================

export default function ProfitPaths({ period = '30d', campaignId, workspaceId, tenantId }) {
  const [sortBy, setSortBy] = useState('revenue');
  const { data, isLoading, error } = useProfitPaths(period, sortBy, tenantId, campaignId, workspaceId);

  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-BLOCKERS-020: Neutral empty state (not red failure)
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="py-12 text-center">
          <GitBranch className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-slate-300 mb-2">Profit Paths Unavailable</h4>
          <p className="text-slate-500 max-w-md mx-auto">
            Unable to load profit path data at this time.
            This may be due to missing campaign attribution or insufficient data for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const paths = data?.paths || [];
  const maxRevenue = paths.length > 0 ? Math.max(...paths.map(p => p.revenue)) : 0;

  return (
    <div className="space-y-6" data-testid="profit-paths-section">
      {/* Header - MA-ARCH-136A: Enhanced with helper text */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-indigo-400" />
            Profit Paths
          </h3>
          {/* MA-ARCH-136A: Helper text for Profit Paths */}
          <p className="text-xs text-slate-500 mt-1">
            {HELPER_DEFINITIONS.profit_paths}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Campaign → Funnel → Lead → Appointment → Client → Revenue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 bg-slate-800 border-slate-700">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Revenue (High→Low)</SelectItem>
              <SelectItem value="profit">Profit (High→Low)</SelectItem>
              <SelectItem value="revenue_per_lead">Rev/Lead</SelectItem>
              <SelectItem value="leads">Most Leads</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Paths"
          value={isLoading ? '...' : data?.total_paths || 0}
          icon={Route}
          color="indigo"
        />
        <SummaryCard
          title="Total Revenue"
          value={isLoading ? '...' : formatCurrency(data?.total_revenue || 0)}
          icon={DollarSign}
          color="green"
        />
        <SummaryCard
          title="Total Leads"
          value={isLoading ? '...' : formatNumber(data?.total_leads || 0)}
          icon={Users}
          color="blue"
        />
        <SummaryCard
          title="Avg Rev/Lead"
          value={isLoading ? '...' : formatCurrency(data?.avg_profit_per_lead || 0)}
          subtitle={paths[0] ? `Top: ${paths[0].campaign_name?.slice(0, 12)}...` : ''}
          icon={Award}
          color="amber"
        />
      </div>

      {/* Path Indicators */}
      {paths.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data?.strongest_path_id && (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              <Trophy className="w-3 h-3 mr-1" />
              Strongest: {paths.find(p => p.path_id === data.strongest_path_id)?.campaign_name?.slice(0, 15) || 'N/A'}
            </Badge>
          )}
          {data?.weakest_path_id && (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Needs Work: {paths.find(p => p.path_id === data.weakest_path_id)?.campaign_name?.slice(0, 15) || 'N/A'}
            </Badge>
          )}
          {data?.highest_roi_path_id && (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
              <Zap className="w-3 h-3 mr-1" />
              Best ROI: {paths.find(p => p.path_id === data.highest_roi_path_id)?.campaign_name?.slice(0, 15) || 'N/A'}
            </Badge>
          )}
        </div>
      )}

      {/* Paths List */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">
          <div className="animate-pulse">Loading profit paths...</div>
        </div>
      ) : paths.length > 0 ? (
        <div className="space-y-4">
          {paths.map((path) => (
            <ProfitPathCard 
              key={path.path_id} 
              path={path} 
              maxRevenue={maxRevenue}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <GitBranch className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-300 mb-2">No Profit Paths Yet</h4>
            <p className="text-slate-500 max-w-md mx-auto">
              Profit paths appear when leads have campaign attribution.
              Ensure your leads have campaign_id set to see path analytics.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Data Source Note */}
      {paths.length > 0 && (
        <div className="text-xs text-slate-500 text-center">
          * Revenue from actual client records. Paths grouped by Campaign → Funnel combination.
        </div>
      )}
    </div>
  );
}
